import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Users, Plus, Trash2, Pencil, Loader2, Phone, Ban, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function MemberRow({ m, onChange }) {
  const toggle = async (active) => {
    try { await api.patch(`/staff-group/${m.contact_id}`, { active }); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = async () => {
    const name = window.prompt("Name", m.name);
    if (name === null) return;
    const phone = window.prompt("Cell number", m.phone);
    if (phone === null) return;
    try { await api.patch(`/staff-group/${m.contact_id}`, { name, phone }); toast.success("Saved"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${m.name} from the staff group?`)) return;
    try { await api.delete(`/staff-group/${m.contact_id}`); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 ${m.active ? "" : "opacity-50"}`} data-testid={`staff-member-${m.contact_id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
          {m.name} {m.opted_out && <Ban className="w-3.5 h-3.5 text-amber-500" title="Opted out (texted STOP)" />}
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</div>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        {m.active ? "Active" : "Inactive"}
        <Switch checked={m.active} onCheckedChange={toggle} data-testid={`staff-active-${m.contact_id}`} />
      </div>
      <button onClick={edit} className="p-1.5 text-slate-400 hover:text-[#D9232E] rounded" data-testid={`staff-edit-${m.contact_id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
      <button onClick={remove} className="p-1.5 text-slate-400 hover:text-[#D9232E] rounded" data-testid={`staff-remove-${m.contact_id}`} title="Remove"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

export default function StaffGroup() {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const syncJR = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/staff-group/sync-jackrabbit");
      toast.success(`Jackrabbit staff synced: ${data.added} added, ${data.updated} updated, ${data.deactivated} set inactive`);
      (data.shared_numbers || []).forEach((s) => toast.warning(`${s.names.join(", ")} share ${s.phone} in Jackrabbit, so they were skipped. Give each their own cell in Jackrabbit or add them here by hand.`, { duration: 12000 }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const load = () => api.get("/staff-group").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/staff-group", { name, phone });
      toast.success(`${name} added`);
      setName(""); setPhone("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const activeCount = data ? data.members.filter((m) => m.active).length : 0;

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="staff-group-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-[#D9232E]" /> Staff Group</h1>
          <Button onClick={syncJR} disabled={syncing} variant="outline" data-testid="staff-sync-jr">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Sync from Jackrabbit
          </Button>
        </div>
        <p className="text-slate-500 text-sm mt-1 mb-6">
          Active staff in Jackrabbit are added automatically every night, and anyone marked inactive there is switched off here. Text them from <b>Send → Staff</b>; each person gets their own text.
        </p>

        <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="staff-add-form">
          <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="h-10" data-testid="staff-add-name" />
          <Input placeholder="Cell number" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-10" data-testid="staff-add-phone" />
          <Button type="submit" disabled={saving || !name.trim() || !phone.trim()} className="h-10 bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="staff-add-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add
          </Button>
        </form>

        {data?.suggestions?.length > 0 && (
          <div className="mb-6" data-testid="staff-suggestions">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">Instructors on your classes. Tap a name, then add their cell number</div>
            <div className="flex flex-wrap gap-1.5">
              {data.suggestions.map((n) => (
                <button key={n} type="button" onClick={() => setName(n)}
                  className={`px-2 py-1 rounded border text-xs transition-colors ${name === n ? "bg-red-50 border-[#D9232E] text-[#D9232E]" : "bg-white border-slate-200 text-slate-700 hover:border-[#D9232E]"}`}
                  data-testid={`staff-suggestion-${n.replace(/\s+/g, "-").toLowerCase()}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-lg bg-white" data-testid="staff-member-list">
          <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-600">{activeCount} active of {data?.members?.length || 0}</div>
          {!data ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : data.members.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No staff yet. Add someone above.</div>
          ) : data.members.map((m) => <MemberRow key={m.contact_id} m={m} onChange={load} />)}
        </div>
      </div>
    </div>
  );
}
